import {
  Home,
  RecurringTaskDtos,
  RecurringTaskListItemResponse,
  Resident,
  TaskType,
} from '@lmc/ts-common'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { useTranslation } from 'react-i18next'
import TaskTags from 'screens/Todos/NewDashboard/components/TaskTags/TaskTags'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { TableProps, Table, Popover } from 'antd'
import { generalConstants } from 'locale/general'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, ImageBouquet } from '@lmc/ui-kit'
import { Tag } from 'antd'
import { Key, useState } from 'react'
import DeleteRecurringTaskModal from './DeleteRecurringTaskModal'
import Actions from '../../../../actions'
import TodoEditScreen from 'components/modals/TodoEditScreen'
import { SortOrder, SorterResult } from 'antd/es/table/interface'
import { ColumnType } from 'antd/lib/table'
import { PAGE_SIZE, PageProps } from '../hooks/useRecurringTasks'

export type SortObject = Partial<Record<keyof RecurringTaskDtos, 1 | -1>>

export const defaultSort = {
  title: 1,
} as const

type ManageTasksTableProps = {
  recurringTasks: RecurringTaskListItemResponse[] | undefined
  pagination:
    | {
        currentPage: number
        totalItems: number
        itemsPerPage: number
      }
    | undefined
  onChange: (props: PageProps) => void
  refetch: () => void
  isLoading: boolean
  onSortChange: (sort: SortObject) => void
  testId?: string
}

const ManageTasksTable = ({
  recurringTasks,
  pagination = {
    currentPage: 1,
    totalItems: 0,
    itemsPerPage: 0,
  },
  onChange,
  isLoading,
  onSortChange,
  refetch,
  testId,
}: ManageTasksTableProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const { t: generalTranslations } = useTranslation(NAMESPACE.GENERAL)
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || '',
  )
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedRecurringTaskId, setSelectedRecurringTaskId] = useState('')

  const preferredNominative = preferredPeopleNominative
    ?.slice(0, -1)
    .toLowerCase()

  const columns: ColumnType<RecurringTaskListItemResponse>[] = [
    {
      title: (
        <LeftmostCellContainer>
          <Text weight="medium">{t(toDosConstants.COLUMN_ITEM)}</Text>
        </LeftmostCellContainer>
      ),
      dataIndex: 'item',
      key: 'item',
      render: (
        item: { icon: string },
        record: RecurringTaskListItemResponse,
      ) => (
        <LeftmostCellContainer>
          <CategoryImageCellContainer color={record.category?.color}>
            <CategoryImage
              src={item?.icon || record.category.icon}
              alt="category"
            />
          </CategoryImageCellContainer>
        </LeftmostCellContainer>
      ),
    },
    {
      title: <Text weight="medium">{t(toDosConstants.COLUMN_TITLE)}</Text>,
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => (
        <CellContainer>
          <Text>{title}</Text>
        </CellContainer>
      ),
      sorter: true,
      defaultSortOrder: getSortOrder(defaultSort.title),
    },
    {
      title: <Text weight="medium">{t(toDosConstants.COLUMN_TASK_TYPE)}</Text>,
      dataIndex: 'taskType',
      key: 'taskType',
      sorter: true,
      render: (type: TaskType) => (
        <CellContainer width={80}>
          <TaskTags typeOnly taskType={type} />
        </CellContainer>
      ),
      className: 'hidden-on-mobile',
    },
    {
      title: (
        <Text weight="medium">{t(toDosConstants.COLUMN_TEAM_MEMBERS)}</Text>
      ),
      dataIndex: 'assignee',
      key: 'teamMembers',
      render: (assignee: []) => (
        <CellContainer width={130}>
          {assignee?.length ? (
            <ImageBouquet
              images={assignee.map(
                (asignee: { picture: string }) => asignee.picture,
              )}
              maximumRenderedItems={3}
              popoverContent={
                <PopoverContent>
                  <MembersPopoverText level={2}>
                    {t(toDosConstants.TEAM_MEMBERS)}
                  </MembersPopoverText>
                  {assignee.map(
                    (asignee: {
                      picture: string
                      id: string
                      name: string
                    }) => (
                      <MembersPopoverOption key={asignee.id}>
                        <Avatar size={18} url={asignee.picture} />
                        <Text>{asignee.name}</Text>
                      </MembersPopoverOption>
                    ),
                  )}
                </PopoverContent>
              }
            />
          ) : (
            <Popover
              placement="top"
              showArrow
              content={
                <>
                  <MembersPopoverText level={2}>
                    {t(toDosConstants.COLUMN_TEAM_MEMBERS).toUpperCase()}
                  </MembersPopoverText>
                  <MembersPopoverOption>
                    <NoPeopleText>
                      {t(toDosConstants.NO_TEAM_MEMBERS_POPOVER, {
                        variable: preferredNominative,
                      })}
                    </NoPeopleText>
                  </MembersPopoverOption>
                </>
              }
              trigger="hover"
            >
              <NoPeopleText>{t(toDosConstants.NO_PEOPLE)}</NoPeopleText>
            </Popover>
          )}
        </CellContainer>
      ),
      className: 'hidden-on-tablet',
    },
    {
      title: <Text weight="medium">{preferredPeopleNominative}</Text>,
      dataIndex: 'resident',
      key: 'residents',
      render: (residents: Resident[]) => (
        <CellContainer width={134}>
          {residents?.length ? (
            <ImageBouquet
              images={residents.map(
                (resident) => resident.picture as unknown as string,
              )}
              maximumRenderedItems={3}
              popoverContent={
                <PopoverContent>
                  <MembersPopoverText level={2}>
                    {preferredPeopleNominative.toUpperCase()}
                  </MembersPopoverText>
                  {residents.map((resident) => (
                    <MembersPopoverOption key={resident.id}>
                      <Avatar
                        size={18}
                        url={resident.picture as unknown as string}
                      />
                      <Text>{getName(resident)}</Text>
                    </MembersPopoverOption>
                  ))}
                </PopoverContent>
              }
            />
          ) : (
            <Popover
              placement="top"
              showArrow
              content={
                <>
                  <MembersPopoverText level={2}>
                    {preferredPeopleNominative.toUpperCase()}
                  </MembersPopoverText>
                  <MembersPopoverOption>
                    <NoPeopleText>
                      {t(toDosConstants.NO_RESIDENTS_POPOVER, {
                        variable: preferredNominative,
                      })}
                    </NoPeopleText>
                  </MembersPopoverOption>
                </>
              }
              trigger="hover"
            >
              <NoPeopleText>{t(toDosConstants.NO_PEOPLE)}</NoPeopleText>
            </Popover>
          )}
        </CellContainer>
      ),
      className: 'hidden-on-tablet',
    },
    {
      title: <Text weight="medium">{t(toDosConstants.COLUMN_FREQUENCY)}</Text>,
      dataIndex: 'recurrence',
      key: 'recurrence',
      render: (recurrence: string) => (
        <CellContainer>
          <CapitalizedText>{recurrence}</CapitalizedText>
        </CellContainer>
      ),
      sorter: true,
      className: 'hidden-on-mobile',
    },
    {
      title: <Text weight="medium">{t(toDosConstants.COLUMN_TIME)}</Text>,
      dataIndex: 'startDate',
      key: 'time',
      render: (startDate: string) => (
        <CellContainer width={60}>
          <Tag>
            <Text>{dayjs(startDate).format('HH:mm')}</Text>
          </Tag>
        </CellContainer>
      ),
      className: 'hidden-on-tablet',
    },
    {
      title: <Text weight="medium">{t(toDosConstants.COLUMN_START_DATE)}</Text>,
      dataIndex: 'startDate',
      key: 'startDate',
      render: (time: string) => (
        <CellContainer width={116}>
          <Text>{dayjs(time).format('DD-MM-YYYY')}</Text>
        </CellContainer>
      ),
      sorter: true,
      className: 'hidden-on-tablet',
    },
    // {
    //   title: <Text weight="medium">{t(toDosConstants.COLUMN_END_DATE)}</Text>,
    //   dataIndex: 'startDate',
    //   key: 'endDate',
    //   render: (time: string) => (
    //     <NoBreakCellContainer>
    //       <Text>{dayjs(time).format('DD-MM-YYYY')}</Text>
    //     </NoBreakCellContainer>
    //   ),
    // },
    {
      title: ' ',
      dataIndex: 'id',
      key: 'delete',
      render: (id, record) => (
        <RightmostCellContainer>
          <CellContainer width={48}>
            <EditButton variant="text" onClick={() => openEditScreen(record)}>
              <Text weight="medium">
                {generalTranslations(generalConstants.EDIT)}
              </Text>
            </EditButton>
            <DeleteButton variant="textDanger" onClick={() => handleDelete(id)}>
              <Text weight="medium">
                {generalTranslations(generalConstants.DELETE)}
              </Text>
            </DeleteButton>
          </CellContainer>
        </RightmostCellContainer>
      ),
    },
  ]

  const getName = (item: { name: { first: string; last: string } }) =>
    `${item?.name?.first} ${item?.name?.last}`

  const dispatch = useDispatch()
  const showEditScreen = useSelector((state: any) => state.modals.editTask)

  const openEditScreen = (recurringTask: RecurringTaskListItemResponse) => {
    const chosenList = recurringTask?.assignee?.length ? 'assignee' : 'resident'

    const idList =
      recurringTask[chosenList]?.map((item) => item.id).join(',') || []

    const formRecurringTask = { ...recurringTask, [chosenList]: idList }

    // some issue here
    dispatch(
      Actions.openModal({
        modalId: 'editTask',
        data: {
          onComplete: onEditComplete,
        },
      }),
    )
    dispatch(
      Actions.addForm({ formId: 'editTask', initial: formRecurringTask }),
    )
  }
  const onEditComplete = () => {
    // navigate('/todos/dashboard')
    refetch()
  }
  const handleDelete = (taskId: string) => {
    setSelectedRecurringTaskId(taskId)
    setOpenDeleteModal(true)
  }

  const handleTableChange: TableProps<RecurringTaskDtos>['onChange'] = (
    pagination,
    filters,
    sorter,
  ) => {
    const sortObject: SortObject = {}

    const applySortOrder = (s: SorterResult<RecurringTaskDtos>) =>
      s.order ? { [s.columnKey as Key]: getSortNumber(s.order) } : {}

    if (Array.isArray(sorter)) {
      sorter.forEach((i) => Object.assign(sortObject, applySortOrder(i)))
    } else {
      Object.assign(sortObject, applySortOrder(sorter))
    }

    onSortChange(sortObject)
  }

  return (
    <div data-testid={testId || 'manage-tasks-table'}>
      <StyledTable<RecurringTaskDtos>
        onChange={handleTableChange}
        loading={isLoading}
        id="manage-todos-table"
        columns={columns}
        dataSource={recurringTasks}
        data-testid={testId ? `${testId}-table` : 'manage-tasks-table-table'}
        pagination={{
          position: ['bottomCenter'],
          showSizeChanger: true,
          pageSize: pagination.itemsPerPage,
          hideOnSinglePage: false,
          pageSizeOptions: pageSizes,
          total: pagination.totalItems,
          current: pagination.currentPage,
          onChange: (page, size) => onChange({ size, index: page }),
        }}
      />
      <DeleteRecurringTaskModal
        recurringTaskId={selectedRecurringTaskId}
        isModalVisible={openDeleteModal}
        setIsModalVisible={setOpenDeleteModal}
        refetch={refetch}
        data-testid={
          testId ? `${testId}-delete-modal` : 'manage-tasks-table-delete-modal'
        }
      />
      {showEditScreen && (
        <EditScreenWrapper
          data-testid={
            testId
              ? `${testId}-edit-screen-wrapper`
              : 'manage-tasks-table-edit-screen-wrapper'
          }
        >
          {/* @ts-ignore */}
          <TodoEditScreen
            data-testid={
              testId
                ? `${testId}-edit-screen`
                : 'manage-tasks-table-edit-screen'
            }
          />
        </EditScreenWrapper>
      )}
    </div>
  )
}

const pageSizes = [PAGE_SIZE, 50, 100]

const getSortOrder = (sortOrder: 1 | -1): SortOrder =>
  sortOrder === 1 ? 'ascend' : 'descend'

const getSortNumber = (sortOrder: 'ascend' | 'descend'): 1 | -1 =>
  sortOrder === 'ascend' ? 1 : -1

const EditScreenWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: fit-content;
  background-color: ${(props) => props.theme.color.neutral.color11};
  z-index: ${(props) => props.theme.properties.zIndex.modal};
`

const StyledTable = styled(Table)`
  .hidden-on-tablet {
    ${({ theme }) => theme.mediaQueries.tabletDown} {
      display: none;
    }
  }

  .hidden-on-mobile {
    ${({ theme }) => theme.mediaQueries.mobileDown} {
      display: none;
    }
  }
` as unknown as typeof Table

const CellContainer = styled.div<{ width?: number }>`
  width: ${(props) => props.width}px;
`

const RightmostCellContainer = styled.div`
  margin-right: 8px;
`

const LeftmostCellContainer = styled.div`
  padding-left: 8px;
`

const NoPeopleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const CapitalizedText = styled(Text)`
  text-transform: capitalize;
`

const CategoryImageCellContainer = styled.div<{ color?: string | undefined }>`
  background-color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  overflow: hidden;
  width: 40px;
  height: 40px;
`

const CategoryImage = styled.img`
  width: 20px;
  height: 20px;
`

const MembersPopoverText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  padding: 0 8px;
`

const MembersPopoverOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 9px;
`

const PopoverContent = styled.div`
  padding: 0 4px;
  max-height: 400px;
  overflow: scroll;
`

const EditButton = styled(Button)``

const DeleteButton = styled(Button)``

export default ManageTasksTable
